import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import { GALLERY, GALLERY_FILTERS, IMG } from "@/data/site";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import FinalCTA from "@/components/home/FinalCTA";

const matches = (item, f) => {
  if (f === "All") return true;
  if (["Residential", "Commercial", "Industrial"].includes(f)) return item.category === f;
  const map = { Roofs: "Roofs", Terraces: "Terraces", Bathrooms: "Bathrooms", Basements: "Basements", "Water Tanks": "Water Tanks" };
  return item.category === map[f];
};

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = useMemo(() => GALLERY.filter((g) => matches(g, filter)), [filter]);

  return (
    <div data-testid="gallery-page">
      <PageHero
        title="Project Gallery"
        subtitle="470+ completed projects across Jaipur — residential, commercial and industrial. Explore a selection of our waterproofing work."
        image={IMG.villa}
        crumbs={[{ label: "Gallery" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {GALLERY_FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === f ? "bg-royal text-white" : "bg-soft-grey text-navy-deep hover:bg-slate-200"}`}>
                {f}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {items.map((g) => (
                <motion.button
                  layout key={g.id}
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.4 }}
                  onClick={() => setActive(g)}
                  data-testid={`gallery-item-${g.id}`}
                  className="group relative rounded-lg overflow-hidden aspect-[4/3] border border-slate-200"
                >
                  <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-navy-deep/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-left translate-y-2 group-hover:translate-y-0 transition-transform">
                    <span className="text-xs text-sky font-semibold">{g.category}</span>
                    <div className="font-display font-bold text-white">{g.title}</div>
                    <div className="flex items-center gap-1 text-xs text-slate-300 mt-1"><MapPin className="h-3 w-3" />{g.area}</div>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-3xl bg-navy-deep border-sky/20 p-0 overflow-hidden" data-testid="lightbox">
          <DialogTitle className="sr-only">{active ? active.title : "Project image"}</DialogTitle>
          {active && (
            <div>
              <img src={active.image} alt={active.title} className="w-full max-h-[70vh] object-cover" />
              <div className="p-5 text-white">
                <span className="text-xs text-sky font-semibold">{active.category}</span>
                <h3 className="font-display text-xl font-extrabold">{active.title}</h3>
                <div className="flex items-center gap-1 text-sm text-slate-300 mt-1"><MapPin className="h-4 w-4" />{active.area}, Jaipur</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <FinalCTA />
    </div>
  );
}
